import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  PhoneOff,
  Activity,
  MessageSquare,
  RefreshCw,
  ArrowLeft,
  Gauge,
  Users,
} from "lucide-react";
import { PIESOCKET_URL } from "../lib/transcript-socket";

const API_BASE = "https://infodation-ai.com/VNAVoicebot2";
const LIST_CONVERSATIONS_URL = `${API_BASE}/list_conversations.php`;
const SUMMARY_API_URL = process.env.REACT_APP_SUMMARY_API_URL || "";

export type Conversation = {
  conversationId: string;
  status: "Live" | "Completed";
  lastActivity: string;
  startedAt: string;
  latestSentiment: number | null;
};

/** Thời gian tương đối (Just now / 5m ago / 2h ago) — dùng Date local nên đã đúng múi giờ. */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffM = Math.floor(diffMs / 60000);
    if (diffM < 1) return "Just now";
    if (diffM < 60) return `${diffM}m ago`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/** Múi giờ của trình duyệt (PC), ví dụ "Europe/Amsterdam". */
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Định dạng theo giờ local của PC (vd. Amsterdam) — API trả về UTC có Z, Date parse đúng rồi format theo timezone này. */
function formatTimeLocal(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      timeZone: browserTimeZone,
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function SentimentGauge({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground text-sm">—</span>;
  const p = Math.min(100, Math.max(0, value));
  const color = p >= 70 ? "text-green-400" : p >= 40 ? "text-amber-400" : "text-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${p >= 70 ? "bg-green-500" : p >= 40 ? "bg-amber-500" : "bg-red-500"}`}
          style={{ width: `${p}%` }}
        />
      </div>
      <span className={`text-sm font-medium ${color}`}>{value}</span>
    </div>
  );
}

type CallEventPayload = {
  conversationId?: string;
  time?: string;
  whom?: string;
  text?: string;
  sentiment?: number | null;
  event?: string;
};

function useConversations(refreshIntervalMs: number) {
  const [data, setData] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveConnected, setLiveConnected] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const res = await fetch(LIST_CONVERSATIONS_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed");
      const fromApi: Conversation[] = (Array.isArray(json.conversations) ? json.conversations : []) as Conversation[];
      setData((prev) => {
        const byId = new Map<string, Conversation>(fromApi.map((c) => [c.conversationId, c]));
        prev.forEach((c) => {
          const apiRow = byId.get(c.conversationId);
          if (apiRow) {
            const prevLast = new Date(c.lastActivity).getTime();
            const apiLast = new Date(apiRow.lastActivity).getTime();
            if (prevLast > apiLast) {
              byId.set(c.conversationId, { ...apiRow, lastActivity: c.lastActivity, status: c.status });
            }
          }
        });
        const list: Conversation[] = Array.from(byId.values());
        list.sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime());
        return list;
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
    const t = setInterval(fetchList, refreshIntervalMs);
    return () => clearInterval(t);
  }, [fetchList, refreshIntervalMs]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(PIESOCKET_URL);
      ws.onopen = () => setLiveConnected(true);
      ws.onclose = () => setLiveConnected(false);
      ws.onerror = () => setLiveConnected(false);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as CallEventPayload;
          const id = msg.conversationId;
          if (!id) return;
          const isCallEnded =
            msg.event === "call_ended" || (typeof msg.text === "string" && msg.text.trim() === "[CALL_ENDED]");
          // Khi cuộc gọi vừa kết thúc, dùng thời gian hiện tại để Dashboard hiển thị "Just now" / "Last activity vừa xong"
          const time = isCallEnded ? new Date().toISOString() : (msg.time || new Date().toISOString());
          const sentiment =
            msg.sentiment != null && msg.sentiment >= 1 && msg.sentiment <= 100 ? msg.sentiment : null;

          setData((prev) => {
            const idx = prev.findIndex((c) => c.conversationId === id);
            const next: Conversation = {
              conversationId: id,
              status: isCallEnded ? "Completed" : "Live",
              lastActivity: time,
              startedAt: idx >= 0 ? prev[idx].startedAt : time,
              latestSentiment: sentiment ?? (idx >= 0 ? prev[idx].latestSentiment : null),
            };
            if (idx >= 0) {
              const out = [...prev];
              out[idx] = {
                ...out[idx],
                lastActivity: next.lastActivity,
                status: isCallEnded ? "Completed" : out[idx].status,
                latestSentiment: next.latestSentiment ?? out[idx].latestSentiment,
              };
              return out.sort(
                (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
              );
            }
            return [next, ...prev].slice(0, 200);
          });
        } catch {
          // ignore parse error
        }
      };
    } catch {
      setLiveConnected(false);
    }
    return () => {
      if (ws) try { ws.close(); } catch { /* ignore */ }
      setLiveConnected(false);
    };
  }, []);

  return { conversations: data, loading, error, refresh: fetchList, liveConnected };
}

function useSummary(conversationId: string | null) {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!conversationId || !SUMMARY_API_URL) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const url = SUMMARY_API_URL.includes("?")
      ? `${SUMMARY_API_URL}&conversationId=${encodeURIComponent(conversationId)}`
      : `${SUMMARY_API_URL}?conversationId=${encodeURIComponent(conversationId)}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled) return;
        const text =
          typeof json?.summary === "string"
            ? json.summary
            : typeof json?.text === "string"
              ? json.text
              : json && typeof json === "object"
                ? JSON.stringify(json).slice(0, 200)
                : null;
        setSummary(text);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  return { summary, loading: !!SUMMARY_API_URL && loading };
}

function ConversationRow({ c }: { c: Conversation }) {
  const { summary, loading } = useSummary(c.conversationId);

  return (
    <div className="glass-panel p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <code className="text-xs font-mono text-muted-foreground truncate max-w-[180px]">
            {c.conversationId}
          </code>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              c.status === "Live"
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                : "bg-muted text-muted-foreground border border-border"
            }`}
          >
            {c.status === "Live" ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </>
            ) : (
              <>
                <PhoneOff className="w-3 h-3" />
                Completed
              </>
            )}
          </span>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span title={formatTimeLocal(c.startedAt)}>Started {formatTime(c.startedAt)} · {formatTimeLocal(c.startedAt)}</span>
          <span title={formatTimeLocal(c.lastActivity)}>Last activity {formatTime(c.lastActivity)} · {formatTimeLocal(c.lastActivity)}</span>
        </div>
      </div>
      <div className="flex sm:items-center gap-4 sm:gap-6">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5" /> Sentiment
          </span>
          <SentimentGauge value={c.latestSentiment} />
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 sm:min-w-[200px]">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> Summary
          </span>
          {SUMMARY_API_URL ? (
            loading ? (
              <span className="text-sm text-muted-foreground italic">Loading…</span>
            ) : summary ? (
              <p className="text-sm text-foreground/90 line-clamp-2">{summary}</p>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )
          ) : (
            <span className="text-xs text-muted-foreground italic">Set REACT_APP_SUMMARY_API_URL</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { conversations, loading, error, refresh, liveConnected } = useConversations(10000);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
              aria-label="Back to Voicebot"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
          <div className="flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-semibold font-display">Call Monitor</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                {liveConnected ? (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live by event
                  </>
                ) : (
                  "Polling every 10s"
                )}
              </p>
            </div>
          </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/client"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium transition-colors"
            >
              <Users className="w-4 h-4" />
              Client page
            </Link>
            <button
              type="button"
              onClick={() => refresh()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}
        {loading && conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground">Loading conversations…</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No conversations yet.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {conversations.map((c) => (
              <li key={c.conversationId}>
                <ConversationRow c={c} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
